import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { formatItemPriceEgp } from '../../utils/formatPrice';

// A genuinely helpful empty state: tells the user *which* filters are
// likely too tight, and lets them remove individual constraints with a
// single tap rather than reopening the modal.

const Wrap = styled.div`
  text-align: center;
  padding: 3rem 1.5rem;
  background: var(--bg-primary);
  border: 1px dashed var(--border-color);
  border-radius: 0.75rem;
  color: var(--text-secondary);
`;

const Emoji = styled.div`
  font-size: 3rem;
  margin-bottom: 0.5rem;
`;

const Title = styled.h3`
  font-size: 1.25rem;
  margin: 0 0 0.5rem;
  color: var(--text-primary);
`;

const Body = styled.p`
  margin: 0 0 1.25rem;
  font-size: 0.95rem;
  line-height: 1.6;
`;

const Suggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Suggestion = styled.button`
  background: white;
  border: 1.5px solid var(--border-color);
  border-radius: 999px;
  padding: 0.45rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  color: var(--text-primary);
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
    transform: translateY(-1px);
  }
`;

const ClearAll = styled.button`
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 0.6rem 1.25rem;
  font-weight: 600;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.95rem;

  &:hover {
    filter: brightness(1.05);
  }
`;

// Return an array of the most likely-to-be-cuplrit filter keys (in priority order).
const culpritKeys = (filters) => {
  const keys = [];
  if (filters.maxPrice) keys.push({ key: 'maxPrice', label: `max price ${formatItemPriceEgp(filters.maxPrice)}` });
  if (filters.minPrice) keys.push({ key: 'minPrice', label: `min price ${formatItemPriceEgp(filters.minPrice)}` });
  if (filters.condition) keys.push({ key: 'condition', label: `"${filters.condition}"` });
  if (filters.color) keys.push({ key: 'color', label: filters.color });
  if (filters.size) keys.push({ key: 'size', label: filters.size });
  if (filters.subcategory) keys.push({ key: 'subcategory', label: filters.subcategory });
  if (filters.book_subcategory) keys.push({ key: 'book_subcategory', label: filters.book_subcategory });
  if (filters.material) keys.push({ key: 'material', label: filters.material });
  if (filters.length) keys.push({ key: 'length', label: filters.length });
  if (filters.brand) keys.push({ key: 'brand', label: filters.brand });
  return keys.slice(0, 3);
};

const EmptyItemsState = ({ filters, onRemoveFilter, onClearAll }) => {
  const { t } = useTranslation();
  const culprits = culpritKeys(filters);

  return (
    <Wrap role="status">
      <Emoji aria-hidden>🪶</Emoji>
      <Title>{t('browse.emptyTitle')}</Title>
      <Body>
        {culprits.length > 0
          ? t('browse.emptyLoosen')
          : t('browse.emptyTryCategory')}
      </Body>
      {culprits.length > 0 && (
        <Suggestions>
          {culprits.map((c) => (
            <Suggestion key={c.key} type="button" onClick={() => onRemoveFilter(c.key)}>
              {t('browse.removeFilter', { label: c.label })}
            </Suggestion>
          ))}
        </Suggestions>
      )}
      <ClearAll type="button" onClick={onClearAll}>
        {t('browse.clearAllFilters')}
      </ClearAll>
    </Wrap>
  );
};

export default EmptyItemsState;
